import { apiRequest } from '../../../shared/api';
import {
  Audit,
  AuditProgramme,
  AuditPlan,
  AuditTeamMember,
  AuditChecklistTemplate,
  AuditChecklistInstance,
  AuditChecklistResponse,
  AuditFindingLink,
  AuditReport,
  AuditDashboardStats,
  AuditStatus,
  ProgrammeStatus,
  PlanStatus,
  AuditType,
  TeamMemberRole,
  ChecklistResponseStatus,
  FindingSeverity,
} from './types';

export const auditService = {
  // Dashboard
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<AuditDashboardStats>(`/audits/analytics/dashboard?organizationId=${organizationId}`),

  // Programmes
  getProgrammes: (params: {
    organizationId?: string;
    year?: number;
    status?: ProgrammeStatus;
  } = {}) => {
    const query = new URLSearchParams();
    query.append('organizationId', params.organizationId || 'ORG-XYZ');
    if (params.year) query.append('year', params.year.toString());
    if (params.status) query.append('status', params.status);
    return apiRequest<AuditProgramme[]>(`/audits/programmes?${query.toString()}`);
  },

  getProgrammeById: (id: string) => apiRequest<AuditProgramme>(`/audits/programmes/${id}`),

  createProgramme: (data: {
    organizationId?: string;
    title: string;
    year: number;
    periodStart: string;
    periodEnd: string;
    ownerId: string;
    items?: any[];
  }) => {
    return apiRequest<AuditProgramme>('/audits/programmes', {
      method: 'POST',
      body: JSON.stringify({
        organizationId: data.organizationId || 'ORG-XYZ',
        ...data,
      }),
    });
  },

  approveProgramme: (id: string, approvedBy: string = 'QUALITY_DIRECTOR') => {
    return apiRequest<AuditProgramme>(`/audits/programmes/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approvedBy }),
    });
  },

  // Plans
  getPlans: (params: {
    organizationId?: string;
    programmeId?: string;
    period?: string;
    status?: PlanStatus;
  } = {}) => {
    const query = new URLSearchParams();
    query.append('organizationId', params.organizationId || 'ORG-XYZ');
    if (params.programmeId) query.append('programmeId', params.programmeId);
    if (params.period) query.append('period', params.period);
    if (params.status) query.append('status', params.status);
    return apiRequest<AuditPlan[]>(`/audits/plans?${query.toString()}`);
  },

  createPlan: (data: {
    organizationId?: string;
    programmeId?: string;
    title: string;
    period: string;
    leadPlannerId: string;
  }) => {
    return apiRequest<AuditPlan>('/audits/plans', {
      method: 'POST',
      body: JSON.stringify({
        organizationId: data.organizationId || 'ORG-XYZ',
        ...data,
      }),
    });
  },

  // Checklists
  getChecklistTemplates: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<AuditChecklistTemplate[]>(`/audits/checklists/templates?organizationId=${organizationId}`),

  createChecklistInstance: (auditId: string, templateId: string) => {
    return apiRequest<AuditChecklistInstance>(`/audits/${auditId}/checklists`, {
      method: 'POST',
      body: JSON.stringify({ templateId }),
    });
  },

  recordChecklistResponse: (
    instanceId: string,
    data: {
      questionId: string;
      responseStatus: ChecklistResponseStatus;
      notes?: string;
      auditorId?: string;
    }
  ) => {
    return apiRequest<AuditChecklistResponse>(`/audits/checklists/${instanceId}/responses`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Audits Register
  getAudits: (params: {
    organizationId?: string;
    status?: AuditStatus;
    department?: string;
    auditType?: AuditType;
    leadAuditorId?: string;
  } = {}) => {
    const query = new URLSearchParams();
    query.append('organizationId', params.organizationId || 'ORG-XYZ');
    if (params.status) query.append('status', params.status);
    if (params.department) query.append('department', params.department);
    if (params.auditType) query.append('auditType', params.auditType);
    if (params.leadAuditorId) query.append('leadAuditorId', params.leadAuditorId);
    return apiRequest<Audit[]>(`/audits?${query.toString()}`);
  },

  getAuditById: (id: string) => apiRequest<Audit>(`/audits/${id}`),

  createAudit: (data: {
    organizationId?: string;
    planId?: string;
    title: string;
    auditType?: AuditType;
    department: string;
    location?: string;
    scopeAndCriteria: string;
    plannedStart: string;
    plannedEnd: string;
    leadAuditorId: string;
    leadAuditorDepartment: string;
    leadAuditorName?: string;
    auditeeFocal: string;
    riskScore?: number;
    isConfidential?: boolean;
    createdBy: string;
  }) => {
    return apiRequest<Audit>('/audits', {
      method: 'POST',
      body: JSON.stringify({
        organizationId: data.organizationId || 'ORG-XYZ',
        ...data,
      }),
    });
  },

  updateStatus: (id: string, status: AuditStatus, actor: string = 'USER', reason?: string) => {
    return apiRequest<Audit>(`/audits/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, actor, reason }),
    });
  },

  addTeamMember: (
    auditId: string,
    data: {
      userId: string;
      userName: string;
      role: TeamMemberRole;
      department: string;
      qualificationSnapshot?: string;
    }
  ) => {
    return apiRequest<AuditTeamMember>(`/audits/${auditId}/team`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  recordOpeningMeeting: (auditId: string, meetingTime: string, actor: string = 'LEAD_AUDITOR') => {
    return apiRequest<Audit>(`/audits/${auditId}/opening-meeting`, {
      method: 'POST',
      body: JSON.stringify({ meetingTime, actor }),
    });
  },

  recordClosingMeeting: (auditId: string, meetingTime: string, actor: string = 'LEAD_AUDITOR') => {
    return apiRequest<Audit>(`/audits/${auditId}/closing-meeting`, {
      method: 'POST',
      body: JSON.stringify({ meetingTime, actor }),
    });
  },

  closeAudit: (auditId: string, closedBy: string = 'QUALITY_MANAGER') => {
    return apiRequest<Audit>(`/audits/${auditId}/close`, {
      method: 'POST',
      body: JSON.stringify({ closedBy }),
    });
  },

  createFinding: (
    auditId: string,
    data: {
      checklistResponseId?: string;
      severity: FindingSeverity;
      title: string;
      clauseReference: string;
      description: string;
      targetClosureDate: string;
    }
  ) => {
    return apiRequest<AuditFindingLink>(`/audits/${auditId}/findings`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  generateReport: (
    auditId: string,
    data: {
      title?: string;
      executiveSummary: string;
      leadAuditorId: string;
    }
  ) => {
    return apiRequest<AuditReport>(`/audits/${auditId}/report`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  approveReport: (auditId: string, qualityManagerId: string = 'QUALITY_MANAGER') => {
    return apiRequest<AuditReport>(`/audits/${auditId}/report/approve`, {
      method: 'POST',
      body: JSON.stringify({ qualityManagerId }),
    });
  },

  publishReport: (auditId: string, publishedBy: string = 'QUALITY_MANAGER') => {
    return apiRequest<AuditReport>(`/audits/${auditId}/report/publish`, {
      method: 'POST',
      body: JSON.stringify({ publishedBy }),
    });
  },

  // SafetyManager365 Quality / Auditor Module Parity APIs
  getPlanView: (year: number = new Date().getFullYear(), departmentId?: number) => {
    const q = new URLSearchParams();
    q.append('year', year.toString());
    if (departmentId) q.append('department_id', departmentId.toString());
    return apiRequest<any>(`/v1/quality/audits/plan-view?${q.toString()}`);
  },

  planAudit: (payload: any) =>
    apiRequest<any>('/v1/quality/audits', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  generateQuestionnaire: (auditId: string, payload: any) =>
    apiRequest<any>(`/v1/quality/audits/${auditId}/questionnaire/generate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  copyQuestionnaire: (auditId: string, sourceAuditId: string) =>
    apiRequest<any>(`/v1/quality/audits/${auditId}/copy-from/${sourceAuditId}`, {
      method: 'POST',
    }),

  acquireSessionLock: (auditId: string, userId: number, overrideActiveSession = false) =>
    apiRequest<any>(`/v1/quality/audits/${auditId}/session/lock`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, override_active_session: overrideActiveSession }),
    }),

  releaseSessionLock: (auditId: string, userId: number) =>
    apiRequest<any>(`/v1/quality/audits/${auditId}/session/unlock`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),

  evaluateQuestionnaireItem: (auditId: string, itemId: number, payload: any) =>
    apiRequest<any>(`/v1/quality/audits/${auditId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  finishAudit: (auditId: string, payload: any) =>
    apiRequest<any>(`/v1/quality/audits/${auditId}/finish`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getAuditorFindings: (params?: Record<string, any>) => {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<any[]>(`/v1/quality/findings${q}`);
  },

  submitCapResponse: (findingId: number, payload: any) =>
    apiRequest<any>(`/v1/quality/findings/${findingId}/cap-response`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  reviewCap: (findingId: number, payload: any) =>
    apiRequest<any>(`/v1/quality/findings/${findingId}/cap-review`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  closeAuditorFinding: (findingId: number, payload: any) =>
    apiRequest<any>(`/v1/quality/findings/${findingId}/close`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  verifyImplementation: (findingId: number, payload: any) =>
    apiRequest<any>(`/v1/quality/findings/${findingId}/verify-implementation`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  rateAuditorFinding: (findingId: number, payload: any) =>
    apiRequest<any>(`/v1/quality/findings/${findingId}/rate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};
